import React, { useState } from "react";
import { Product, ProductChildReference, ProductLinkedFunnel } from "./ProductInterface";
import { patchWebsiteProductPricing, putProduct } from "../../services/products/productAxios";
import type { ProductFunnelContext } from "../../services/products/productAxios";
import { formatCurrency, getCurrencySymbol } from "../../utils/formatters";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import { ProductAction, ProductState } from "../../reducers/productPage/ProductPageReducer";
import { AlertTriangle, ExternalLink, Eye, Layers3, Pencil, Save, X } from "lucide-react";
import { Currency, getCountriesList } from "../../const/countryList";
import { AnimatePresence, motion } from "framer-motion";
import { builderUrl } from "../../utils/builderUrl";

interface ProductsTableProps {
  productsState: ProductState;
  dispatchProducts: (action: ProductAction) => void;
  selectedProductId?: string;
  selectedFunnelId?: string;
  funnelContext?: ProductFunnelContext | null;
  footer?: React.ReactNode;
}

type ProductDraft = {
  name: string;
  price: string;
  country_code: string;
  currency_code: string;
};

type FunnelPricingDraft = {
  price: string;
  country_code: string;
  currency_code: string;
};

type PendingCurrencyChange =
  | { scope: "base"; product: Product; country_code: string; currency_code: string }
  | { scope: "funnel"; funnel: ProductLinkedFunnel; country_code: string; currency_code: string };

type FunnelContextProduct = ProductFunnelContext["products"][number];

type FunnelContextItem = {
  contextProduct: FunnelContextProduct;
  parentProduct: Product;
  displayProduct: Product | ProductChildReference;
  productImage?: string;
  isVersion: boolean;
  linkedFunnels: ProductLinkedFunnel[];
  countryCode: string;
  currencyCode: string;
  currencySymbol: string;
  effectivePrice: number;
  hasOfferPrice: boolean;
  offerPriceLabel: string;
  isSelected: boolean;
};

export default function ProductsTable({
  productsState,
  dispatchProducts,
  selectedProductId,
  selectedFunnelId,
  funnelContext,
  footer,
}: ProductsTableProps) {
  const { language, country } = useAuth();
  const countryOptions = getCountriesList(language);
  const [productChildrenModal, setProductChildrenModal] = useState<Product | null>(null);
  const [baseDrafts, setBaseDrafts] = useState<Record<string, ProductDraft>>({});
  const [versionDrafts, setVersionDrafts] = useState<Record<string, ProductDraft>>({});
  const [savingBaseProducts, setSavingBaseProducts] = useState<Set<string>>(new Set());
  const [savingVersions, setSavingVersions] = useState<Set<string>>(new Set());
  const [pendingSharedProductSave, setPendingSharedProductSave] = useState<Product | null>(null);
  const [showAllProductsInFunnelMode, setShowAllProductsInFunnelMode] = useState(false);
  const [expandedVersionProductIds, setExpandedVersionProductIds] = useState<Set<string>>(new Set());
  const [funnelPricingDrafts, setFunnelPricingDrafts] = useState<Record<string, FunnelPricingDraft>>({});
  const [savingFunnelPricing, setSavingFunnelPricing] = useState<Set<string>>(new Set());
  const [pendingCurrencyChange, setPendingCurrencyChange] = useState<PendingCurrencyChange | null>(null);
  const [focusedPriceField, setFocusedPriceField] = useState<string | null>(null);
  const [showAllProductImages, setShowAllProductImages] = useState(false);

  const getCurrencyByCountry = (countryCode?: string | null, fallbackCurrency?: string | null) =>
    countryOptions.find((option) => option.country === countryCode)?.currency || fallbackCurrency || Currency.COP;

  const getCountryName = (countryCode?: string | null) =>
    countryOptions.find((option) => option.country === countryCode)?.name || countryCode || "Sin país";

  const parseNullableNumber = (value?: number | string | null) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getEffectiveFunnelPrice = (source?: Partial<ProductLinkedFunnel | FunnelContextProduct> | null) =>
    parseNullableNumber(source?.effective_price) ??
    parseNullableNumber(source?.offer_price) ??
    parseNullableNumber(source?.price);

  const hasFunnelOfferPrice = (source?: Partial<ProductLinkedFunnel | FunnelContextProduct> | null) =>
    parseNullableNumber(source?.offer_price) !== null ||
    parseNullableNumber(source?.effective_price) !== null;

  const getFunnelCountryCode = (
    source?: Partial<ProductLinkedFunnel | FunnelContextProduct> | null,
    fallback?: string | null
  ) =>
    source?.effective_country_code ||
    source?.offer_country_code ||
    source?.country_code ||
    fallback ||
    country ||
    "CO";

  const getFunnelCurrencyCode = (
    source?: Partial<ProductLinkedFunnel | FunnelContextProduct> | null,
    fallbackCountry?: string | null,
    fallbackCurrency?: string | null
  ) =>
    source?.effective_currency_code ||
    source?.offer_currency_code ||
    source?.currency_code ||
    getCurrencyByCountry(fallbackCountry, fallbackCurrency);

  const getOfferPriceLabel = (source?: Partial<ProductLinkedFunnel | FunnelContextProduct> | null) =>
    hasFunnelOfferPrice(source) ? "Oferta configurada" : "Pendiente de migración";

  const sanitizePriceInput = (value: string) => value.replace(/[^\d.,]/g, "");

  const parsePriceInput = (value?: string | number | null) => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;

    const cleanedValue = value.trim().replace(/\s/g, "");
    if (!cleanedValue) return null;

    let normalizedValue = cleanedValue;
    if (cleanedValue.includes(".") && cleanedValue.includes(",")) {
      normalizedValue = cleanedValue.replace(/\./g, "").replace(",", ".");
    } else if (cleanedValue.includes(",")) {
      normalizedValue = cleanedValue.replace(",", ".");
    } else if (/^\d{1,3}(\.\d{3})+$/.test(cleanedValue)) {
      normalizedValue = cleanedValue.replace(/\./g, "");
    }

    const parsed = Number(normalizedValue);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatPriceInput = (value?: string | number | null) => {
    const parsed = parsePriceInput(value);
    if (parsed === null) return "";
    return new Intl.NumberFormat("es-CO", {
      minimumFractionDigits: 0,
      maximumFractionDigits: parsed % 1 === 0 ? 0 : 2,
    }).format(parsed);
  };

  const normalizeEditablePrice = (value?: string | number | null) => {
    const parsed = parsePriceInput(value);
    return parsed === null ? "" : String(parsed);
  };

  const buildEditorUrl = (ownerId: string, funnelId?: string | null) =>
    builderUrl(funnelId, "funnel", ownerId) || "";

  function getDirectLinkedFunnels(product: Product): ProductLinkedFunnel[] {
    if (product.linked_funnels?.length) return product.linked_funnels;
    if (!product.website_id) return [];

    return [{
      website_id: product.website_id,
      website_name: product.website_name || "Embudo sin nombre",
      product_type: "PRINCIPAL",
      country_code: product.country_code,
      currency_code: product.currency_code,
      status: null,
    }];
  }

  function getChildLinkedFunnels(childProduct: ProductChildReference): ProductLinkedFunnel[] {
    if (childProduct.linked_funnels?.length) return childProduct.linked_funnels;
    if (!childProduct.website_id) return [];

    return [{
      website_id: childProduct.website_id,
      website_name: childProduct.website_name || "Embudo sin nombre",
      product_type: "PRINCIPAL",
      country_code: childProduct.country_code,
      currency_code: childProduct.currency_code,
      status: null,
    }];
  }

  const getLinkedFunnelsCount = (product: Product) => {
    const linkedFunnelIds = new Set<string>();

    getDirectLinkedFunnels(product).forEach((funnel) => linkedFunnelIds.add(funnel.website_id));
    product.child_products?.forEach((childProduct) => {
      getChildLinkedFunnels(childProduct).forEach((funnel) => linkedFunnelIds.add(funnel.website_id));
    });

    return linkedFunnelIds.size;
  };

  const getProductLinkedCountries = (product: Product) => {
    const countryCodes = new Set<string>();

    getDirectLinkedFunnels(product).forEach((funnel) => {
      if (funnel.country_code) countryCodes.add(funnel.country_code);
    });
    product.child_products?.forEach((childProduct) => {
      getChildLinkedFunnels(childProduct).forEach((funnel) => {
        if (funnel.country_code) countryCodes.add(funnel.country_code);
      });
    });

    if (countryCodes.size === 0) {
      countryCodes.add(product.country_code || country || "CO");
    }

    return Array.from(countryCodes);
  };

  const getLinkedFunnelsLabel = (product: Product) => {
    const linkedFunnelsCount = getLinkedFunnelsCount(product);

    if (linkedFunnelsCount === 0) return "Editar producto";
    if (linkedFunnelsCount === 1) return "1 embudo vinculado";
    return `${linkedFunnelsCount} embudos vinculados`;
  };

  const getFunnelStatusLabel = (status?: string | null) => {
    const normalizedStatus = status?.toUpperCase();

    if (!normalizedStatus) return "Sin estado";
    if (normalizedStatus === "BUILDING") return "Creado";
    if (normalizedStatus === "PUBLISH") return "Publicado";
    if (normalizedStatus === "PRIVATE") return "Privado";
    if (normalizedStatus === "HIDDEN") return "Oculto";

    return normalizedStatus;
  };

  const getFunnelNamesPreview = (funnels: ProductLinkedFunnel[]) => {
    const names = funnels.map((funnel) => funnel.website_name || "Embudo sin nombre");
    const visibleNames = names.slice(0, 3).join(", ");
    const remainingCount = Math.max(0, names.length - 3);

    return remainingCount > 0 ? `${visibleNames} y ${remainingCount} más` : visibleNames;
  };

  const getProductRoleLabel = (productType?: string | null) => {
    const normalizedType = productType?.toUpperCase();

    if (normalizedType === "PRINCIPAL") return "Principal";
    if (normalizedType === "BUMP_OFFER") return "Order bump";
    if (normalizedType === "UP_SELL") return "Upsell";
    if (normalizedType === "DOWN_SELL") return "Downsell";

    return productType || "Sin uso";
  };

  const isProductInSelectedFunnel = (product: Product) => {
    if (!selectedFunnelId) return false;

    return (
      getDirectLinkedFunnels(product).some((funnel) => funnel.website_id === selectedFunnelId) ||
      product.child_products?.some((childProduct) =>
        getChildLinkedFunnels(childProduct).some((funnel) => funnel.website_id === selectedFunnelId)
      )
    );
  };

  const isSelectedProductRow = (product: Product) => {
    if (!selectedProductId) return false;

    return (
      product.id === selectedProductId ||
      product.child_products?.some((childProduct) => childProduct.id === selectedProductId)
    );
  };

  const getFunnelContextItems = (): FunnelContextItem[] => {
    if (!funnelContext) return [];

    return funnelContext.products.reduce<FunnelContextItem[]>((items, contextProduct) => {
      const parentProduct = productsState.products.find((product) =>
        product.id === contextProduct.parent_product_id ||
        product.id === contextProduct.product_id ||
        product.child_products?.some((childProduct) => childProduct.id === contextProduct.product_id)
      );

      if (!parentProduct) return items;

      const versionProduct = parentProduct.child_products?.find((childProduct) => childProduct.id === contextProduct.product_id);
      const isVersion = contextProduct.relation_kind === "VERSION" || Boolean(versionProduct);
      const displayProduct = versionProduct || parentProduct;
      const linkedFunnels = isVersion && versionProduct
        ? getChildLinkedFunnels(versionProduct)
        : getDirectLinkedFunnels(parentProduct);
      const selectedFunnel = selectedFunnelId
        ? linkedFunnels.find((funnel) => funnel.website_id === selectedFunnelId)
        : undefined;
      const priceSource = selectedFunnel || contextProduct;
      const countryCode = getFunnelCountryCode(priceSource, displayProduct.country_code || parentProduct.country_code);
      const currencyCode = getFunnelCurrencyCode(priceSource, countryCode, displayProduct.currency_code || parentProduct.currency_code);
      const currencySymbol = getCurrencySymbol(currencyCode || countryCode || country);
      const effectivePrice =
        getEffectiveFunnelPrice(contextProduct) ??
        getEffectiveFunnelPrice(selectedFunnel) ??
        Number(displayProduct.price ?? 0);
      const productImage = displayProduct.images?.[0] || parentProduct.images?.[0];
      const contextHasOfferPrice = hasFunnelOfferPrice(contextProduct);

      items.push({
        contextProduct,
        parentProduct,
        displayProduct,
        productImage,
        isVersion,
        linkedFunnels,
        countryCode,
        currencyCode,
        currencySymbol,
        effectivePrice,
        hasOfferPrice: contextHasOfferPrice || hasFunnelOfferPrice(selectedFunnel),
        offerPriceLabel: contextHasOfferPrice ? getOfferPriceLabel(contextProduct) : getOfferPriceLabel(selectedFunnel),
        isSelected:
          selectedProductId === contextProduct.product_id ||
          selectedProductId === parentProduct.id ||
          selectedProductId === displayProduct.id,
      });

      return items;
    }, []);
  };

  const getSelectedFunnelContextProduct = (product: Product) => {
    if (!funnelContext || !selectedFunnelId) return null;

    return funnelContext.products.find((contextProduct) =>
      contextProduct.parent_product_id === product.id ||
      contextProduct.product_id === product.id ||
      product.child_products?.some((childProduct) => childProduct.id === contextProduct.product_id)
    ) || null;
  };

  const mergeSelectedFunnelContext = (
    funnels: ProductLinkedFunnel[],
    product: Product
  ): ProductLinkedFunnel[] => {
    const contextProduct = getSelectedFunnelContextProduct(product);
    if (!contextProduct || !selectedFunnelId) return funnels;

    const contextFunnel: ProductLinkedFunnel = {
      website_id: funnelContext?.website_id || selectedFunnelId,
      website_name: funnelContext?.website_name || "Embudo seleccionado",
      product_type: contextProduct.product_type || "PRINCIPAL",
      price: contextProduct.price,
      offer_price: contextProduct.offer_price,
      effective_price: contextProduct.effective_price,
      country_code: contextProduct.country_code,
      currency_code: contextProduct.currency_code,
      offer_country_code: contextProduct.offer_country_code,
      offer_currency_code: contextProduct.offer_currency_code,
      effective_country_code: contextProduct.effective_country_code,
      effective_currency_code: contextProduct.effective_currency_code,
      status: null,
    };

    const didMerge = funnels.some((funnel) =>
      funnel.website_id === selectedFunnelId &&
      (funnel.product_type || "PRINCIPAL") === (contextFunnel.product_type || "PRINCIPAL")
    );

    if (didMerge) {
      return funnels.map((funnel) =>
        funnel.website_id === selectedFunnelId &&
        (funnel.product_type || "PRINCIPAL") === (contextFunnel.product_type || "PRINCIPAL")
          ? { ...funnel, ...contextFunnel }
          : funnel
      );
    }

    return [contextFunnel, ...funnels];
  };

  const getBaseDraft = (product: Product) =>
    baseDrafts[product.id] || {
      name: product.name || "",
      price: String(product.price ?? 0),
      country_code: product.country_code || country || "CO",
      currency_code: product.currency_code || getCurrencyByCountry(product.country_code || country || "CO"),
    };

  const updateBaseDraft = (product: Product, updates: Partial<ProductDraft>) => {
    setBaseDrafts((current) => ({
      ...current,
      [product.id]: {
        ...getBaseDraft(product),
        ...updates,
      },
    }));
  };

  const getVersionDraft = (childProduct: ProductChildReference, parentProduct: Product) =>
    versionDrafts[childProduct.id] || {
      name: childProduct.name || "",
      price: String(childProduct.price ?? 0),
      country_code: childProduct.country_code || parentProduct.country_code || country || "CO",
    };

  const updateVersionDraft = (
    childProduct: ProductChildReference,
    parentProduct: Product,
    updates: Partial<ProductDraft>
  ) => {
    setVersionDrafts((current) => ({
      ...current,
      [childProduct.id]: {
        ...getVersionDraft(childProduct, parentProduct),
        ...updates,
      },
    }));
  };

  const getFunnelPricingKey = (funnel: ProductLinkedFunnel) =>
    `${funnel.website_id}:${funnel.product_type || "PRINCIPAL"}`;

  const getFunnelPricingDraft = (funnel: ProductLinkedFunnel): FunnelPricingDraft => {
    const key = getFunnelPricingKey(funnel);
    const countryCode = getFunnelCountryCode(funnel);
    const currencyCode = getFunnelCurrencyCode(funnel, countryCode);
    const price = getEffectiveFunnelPrice(funnel);

    return funnelPricingDrafts[key] || {
      price: price !== null ? String(price) : "",
      country_code: countryCode,
      currency_code: currencyCode,
    };
  };

  const updateFunnelPricingDraft = (funnel: ProductLinkedFunnel, updates: Partial<FunnelPricingDraft>) => {
    setFunnelPricingDrafts((current) => ({
      ...current,
      [getFunnelPricingKey(funnel)]: {
        ...getFunnelPricingDraft(funnel),
        ...updates,
      },
    }));
  };

  const normalizePriceString = (value?: string | number | null) => {
    return normalizeEditablePrice(value);
  };

  const baseProductHasChanges = (product: Product) => {
    const draft = getBaseDraft(product);
    const originalCountryCode = product.country_code || country || "CO";
    const originalCurrencyCode = product.currency_code || getCurrencyByCountry(originalCountryCode);

    return (
      draft.name.trim() !== product.name ||
      normalizePriceString(draft.price) !== normalizePriceString(product.price) ||
      draft.currency_code !== originalCurrencyCode
    );
  };

  const funnelPricingHasChanges = (funnel: ProductLinkedFunnel) => {
    const draft = getFunnelPricingDraft(funnel);
    const countryCode = getFunnelCountryCode(funnel);
    const originalCurrencyCode = getFunnelCurrencyCode(funnel, countryCode);

    return (
      normalizePriceString(draft.price) !== normalizePriceString(getEffectiveFunnelPrice(funnel)) ||
      draft.country_code !== countryCode ||
      draft.currency_code !== originalCurrencyCode
    );
  };

  const resetProductModalDrafts = (product: Product) => {
    setBaseDrafts((current) => {
      const next = { ...current };
      delete next[product.id];
      return next;
    });

    getDirectLinkedFunnels(product).forEach((funnel) => {
      setFunnelPricingDrafts((current) => {
        const next = { ...current };
        delete next[getFunnelPricingKey(funnel)];
        return next;
      });
    });
  };

  const applyPendingCurrencyChange = () => {
    if (!pendingCurrencyChange) return;

    if (pendingCurrencyChange.scope === "base") {
      updateBaseDraft(pendingCurrencyChange.product, {
        country_code: pendingCurrencyChange.country_code,
        currency_code: pendingCurrencyChange.currency_code,
      });
    } else {
      updateFunnelPricingDraft(pendingCurrencyChange.funnel, {
        country_code: pendingCurrencyChange.country_code,
        currency_code: pendingCurrencyChange.currency_code,
      });
    }

    setPendingCurrencyChange(null);
  };

  const saveFunnelPricing = async (
    funnel: ProductLinkedFunnel,
    updates?: Partial<FunnelPricingDraft>
  ) => {
    const draft = {
      ...getFunnelPricingDraft(funnel),
      ...updates,
    };
    const price = parsePriceInput(draft.price);
    const productType = funnel.product_type || "PRINCIPAL";
    const pricingKey = getFunnelPricingKey(funnel);

    if (price === null || price < 0) {
      toast.error("Precio inválido");
      return;
    }

    setSavingFunnelPricing((current) => new Set(current).add(pricingKey));
    try {
      await patchWebsiteProductPricing(funnel.website_id, productType, {
        price,
        country_code: draft.country_code,
        currency_code: draft.currency_code,
      });

      const mergeFunnel = (item: ProductLinkedFunnel): ProductLinkedFunnel =>
        item.website_id === funnel.website_id && (item.product_type || "PRINCIPAL") === productType
          ? {
              ...item,
              offer_price: price,
              effective_price: price,
              offer_country_code: draft.country_code,
              effective_country_code: draft.country_code,
              offer_currency_code: draft.currency_code,
              effective_currency_code: draft.currency_code,
            }
          : item;

      const nextProducts = productsState.products.map((product) => ({
        ...product,
        linked_funnels: product.linked_funnels?.map(mergeFunnel),
        child_products: product.child_products?.map((childProduct) => ({
          ...childProduct,
          linked_funnels: childProduct.linked_funnels?.map(mergeFunnel),
        })),
      }));

      dispatchProducts({ type: "SET_PRODUCTS", payload: nextProducts });
      if (productChildrenModal) {
        const nextModal = nextProducts.find((product) => product.id === productChildrenModal.id);
        if (nextModal) setProductChildrenModal(nextModal);
      }
      setFunnelPricingDrafts((current) => ({
        ...current,
        [pricingKey]: {
          price: String(price),
          country_code: draft.country_code,
          currency_code: draft.currency_code,
        },
      }));
      toast.success("Oferta actualizada");
    } catch (error) {
      console.error("Error updating funnel pricing", error);
      toast.error((error as any)?.response?.data?.message || "No se pudo actualizar la oferta");
    } finally {
      setSavingFunnelPricing((current) => {
        const next = new Set(current);
        next.delete(pricingKey);
        return next;
      });
    }
  };

  const saveProductModalChanges = async (product: Product) => {
    const baseDraft = getBaseDraft(product);
    const baseChanged = baseProductHasChanges(product);
    const productFunnels = mergeSelectedFunnelContext(getDirectLinkedFunnels(product), product);
    const changedFunnels = productFunnels.filter(funnelPricingHasChanges);

    if (!baseChanged && changedFunnels.length === 0) return;

    const productName = baseDraft.name.trim();
    const productPrice = parsePriceInput(baseDraft.price);

    if (!productName) {
      toast.error("Nombre inválido");
      return;
    }
    if (productPrice === null || productPrice < 0) {
      toast.error("Precio inválido");
      return;
    }

    for (const funnel of changedFunnels) {
      const draft = getFunnelPricingDraft(funnel);
      const price = parsePriceInput(draft.price);
      if (price === null || price < 0) {
        toast.error(`Precio inválido en ${funnel.website_name || "un embudo"}`);
        return;
      }
    }

    const changedFunnelKeys = changedFunnels.map(getFunnelPricingKey);
    setSavingBaseProducts((current) => new Set(current).add(product.id));
    setSavingFunnelPricing((current) => {
      const next = new Set(current);
      changedFunnelKeys.forEach((key) => next.add(key));
      return next;
    });

    try {
      let mergedBaseProduct = product;
      if (baseChanged) {
        const productPayload: Product = {
          ...product,
          name: productName,
          price: productPrice,
          country_code: baseDraft.country_code || product.country_code || country || "CO",
          currency_code: baseDraft.currency_code || product.currency_code || getCurrencyByCountry(baseDraft.country_code),
        };
        const updatedProduct = await putProduct(productPayload);
        mergedBaseProduct = {
          ...productPayload,
          ...updatedProduct,
          child_products: product.child_products,
          linked_funnels: product.linked_funnels,
        };
      }

      await Promise.all(changedFunnels.map((funnel) => {
        const draft = getFunnelPricingDraft(funnel);
        return patchWebsiteProductPricing(funnel.website_id, funnel.product_type || "PRINCIPAL", {
          price: parsePriceInput(draft.price) ?? 0,
          country_code: draft.country_code,
          currency_code: draft.currency_code,
        });
      }));

      const mergeFunnel = (item: ProductLinkedFunnel): ProductLinkedFunnel => {
        const changedFunnel = changedFunnels.find((funnel) =>
          funnel.website_id === item.website_id &&
          (funnel.product_type || "PRINCIPAL") === (item.product_type || "PRINCIPAL")
        );
        if (!changedFunnel) return item;

        const draft = getFunnelPricingDraft(changedFunnel);
        const price = parsePriceInput(draft.price) ?? 0;
        return {
          ...item,
          offer_price: price,
          effective_price: price,
          offer_country_code: draft.country_code,
          effective_country_code: draft.country_code,
          offer_currency_code: draft.currency_code,
          effective_currency_code: draft.currency_code,
        };
      };

      const nextProducts = productsState.products.map((item) => {
        const baseItem = item.id === product.id ? mergedBaseProduct : item;
        return {
          ...baseItem,
          linked_funnels: baseItem.linked_funnels?.map(mergeFunnel),
          child_products: baseItem.child_products?.map((childProduct) => ({
            ...childProduct,
            linked_funnels: childProduct.linked_funnels?.map(mergeFunnel),
          })),
        };
      });

      dispatchProducts({ type: "SET_PRODUCTS", payload: nextProducts });
      const nextModal = nextProducts.find((item) => item.id === product.id);
      if (nextModal) setProductChildrenModal(nextModal);
      resetProductModalDrafts(nextModal || mergedBaseProduct);
      toast.success("Cambios guardados");
    } catch (error) {
      console.error("Error saving product modal changes", error);
      toast.error((error as any)?.response?.data?.message || "No se pudieron guardar los cambios");
    } finally {
      setSavingBaseProducts((current) => {
        const next = new Set(current);
        next.delete(product.id);
        return next;
      });
      setSavingFunnelPricing((current) => {
        const next = new Set(current);
        changedFunnelKeys.forEach((key) => next.delete(key));
        return next;
      });
    }
  };

  const saveBaseProduct = async (product: Product, confirmedImpact = false) => {
    const draft = getBaseDraft(product);
    const name = draft.name.trim();
    if (!name) {
      toast.error("Nombre inválido");
      return;
    }

    const price = parsePriceInput(draft.price);
    if (price === null || price < 0) {
      toast.error("Precio inválido");
      return;
    }

    const directFunnels = getDirectLinkedFunnels(product);
    const countryCode = draft.country_code || product.country_code || country || "CO";
    const currencyCode = draft.currency_code || product.currency_code || getCurrencyByCountry(countryCode);

    if (directFunnels.length > 1 && !confirmedImpact) {
      setPendingSharedProductSave(product);
      return;
    }

    const nextProduct = {
      ...product,
      name,
      price,
      country_code: countryCode,
      currency_code: currencyCode,
      linked_funnels: getDirectLinkedFunnels(product),
    };

    setSavingBaseProducts((current) => new Set(current).add(product.id));
    try {
      const updatedProduct = await putProduct(nextProduct);
      const mergedProduct = {
        ...nextProduct,
        ...updatedProduct,
        child_products: product.child_products,
        linked_funnels: nextProduct.linked_funnels,
      };
      const nextProducts = productsState.products.map((item) =>
        item.id === product.id ? mergedProduct : item
      );
      dispatchProducts({ type: "SET_PRODUCTS", payload: nextProducts });
      setProductChildrenModal(mergedProduct);
      toast.success(directFunnels.length > 1 ? `Producto actualizado en ${directFunnels.length} embudos` : "Producto actualizado");
    } catch (error) {
      console.error("Error updating base product", error);
      toast.error((error as any)?.response?.data?.message || "No se pudo actualizar el producto compartido");
    } finally {
      setPendingSharedProductSave(null);
      setSavingBaseProducts((current) => {
        const next = new Set(current);
        next.delete(product.id);
        return next;
      });
    }
  };

  const saveProductVersion = async (parentProduct: Product, childProduct: ProductChildReference) => {
    const draft = getVersionDraft(childProduct, parentProduct);
    const name = draft.name.trim();
    if (!name) {
      toast.error("Nombre inválido");
      return;
    }

    const price = parsePriceInput(draft.price);
    if (price === null || price < 0) {
      toast.error("Precio inválido");
      return;
    }

    const childFunnels = getChildLinkedFunnels(childProduct);
    const countryCode = childProduct.country_code || parentProduct.country_code || country || "CO";
    const currencyCode = childProduct.currency_code || getCurrencyByCountry(countryCode);
    const nextChild: ProductChildReference = {
      ...childProduct,
      name,
      price,
      country_code: countryCode,
      currency_code: currencyCode,
      linked_funnels: childFunnels,
    };
    const productPayload: Product = {
      ...parentProduct,
      id: childProduct.id,
      parent_id: childProduct.parent_id || parentProduct.id,
      name,
      price,
      price_formatted: childProduct.price_formatted,
      country_code: countryCode,
      currency_code: currencyCode,
      available: childProduct.available ?? true,
      images: childProduct.images?.length ? childProduct.images : parentProduct.images,
      description: childProduct.description ?? parentProduct.description,
      website_id: childProduct.website_id,
      website_name: childProduct.website_name,
      linked_funnels: nextChild.linked_funnels,
      child_products: [],
    };

    setSavingVersions((current) => new Set(current).add(childProduct.id));
    try {
      const updatedChild = await putProduct(productPayload);
      const mergedChild: ProductChildReference = {
        ...nextChild,
        ...updatedChild,
        parent_id: childProduct.parent_id || parentProduct.id,
        linked_funnels: nextChild.linked_funnels,
      };
      const nextParentProduct = {
        ...parentProduct,
        child_products: (parentProduct.child_products || []).map((item) =>
          item.id === childProduct.id ? mergedChild : item
        ),
      };
      const nextProducts = productsState.products.map((item) =>
        item.id === parentProduct.id ? nextParentProduct : item
      );
      dispatchProducts({ type: "SET_PRODUCTS", payload: nextProducts });
      setProductChildrenModal(nextParentProduct);
      toast.success("Variante actualizada");
    } catch (error) {
      console.error("Error updating legacy product variant", error);
      toast.error((error as any)?.response?.data?.message || "No se pudo actualizar la variante");
    } finally {
      setSavingVersions((current) => {
        const next = new Set(current);
        next.delete(childProduct.id);
        return next;
      });
    }
  };

  const startEditing = (product: Product) => {
    setProductChildrenModal(product);
  };

  const renderFunnelRows = (
    funnels: ProductLinkedFunnel[],
    ownerId: string,
    highlightedFunnelId?: string
  ) => {
    if (funnels.length === 0) {
      return (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-5 text-sm text-gray-500">
          No hay embudos vinculados directamente a este producto.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] table-fixed text-sm">
          <thead className="border-b border-gray-100 text-left text-[11px] font-semibold uppercase text-gray-400">
            <tr>
              <th className="w-[36%] px-4 py-3">Detalle</th>
              <th className="w-[26%] px-4 py-3">País / moneda</th>
              <th className="w-[24%] px-4 py-3">Precio</th>
		              <th className="w-[14%] px-4 py-3 text-right">Abrir</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {funnels.map((funnel) => {
              const isHighlighted = funnel.website_id === highlightedFunnelId;
              const funnelCountryCode = getFunnelCountryCode(funnel);
              const funnelCurrencyCode = getFunnelCurrencyCode(funnel, funnelCountryCode);
              const funnelCurrencySymbol = getCurrencySymbol(funnelCurrencyCode || funnelCountryCode || country);
              const funnelPrice = getEffectiveFunnelPrice(funnel);
              const pricingDraft = getFunnelPricingDraft(funnel);
              const pricingKey = getFunnelPricingKey(funnel);
              const isSavingPricing = savingFunnelPricing.has(pricingKey);
              return (
	                <tr key={`${funnel.website_id}-${funnel.product_type}`} className={isHighlighted ? "bg-blue-50/70" : "bg-white"}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-medium text-gray-900">{funnel.website_name || "Embudo sin nombre"}</p>
	                      {isHighlighted && (
	                        <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
	                          Embudo actual
	                        </span>
	                      )}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-gray-400">{funnel.website_id}</p>
                    <span className="mt-2 inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                      {getProductRoleLabel(funnel.product_type)}
                    </span>
                  </td>
	                  <td className="px-4 py-3">
	                    <div className="grid w-full max-w-[12rem] grid-cols-[4rem_5rem] gap-4">
	                      <div className="group relative min-w-0">
                        <select
                          value={pricingDraft.country_code}
                          onChange={(event) => {
                            const countryCode = event.target.value;
                            const currencyCode = getCurrencyByCountry(countryCode, pricingDraft.currency_code);
                            if (countryCode !== pricingDraft.country_code || currencyCode !== pricingDraft.currency_code) {
                              setPendingCurrencyChange({
                                scope: "funnel",
                                funnel,
                              country_code: countryCode,
                                currency_code: currencyCode,
                              });
                            }
                          }}
                          disabled={isSavingPricing}
	                          className="h-8 w-full appearance-none border-0 border-b border-transparent bg-transparent px-0 pr-5 text-sm font-semibold text-gray-900 outline-none transition-colors focus:border-[#3B4A9F] disabled:text-gray-400"
                        >
                          {countryOptions.map((countryOption) => (
                            <option key={countryOption.country} value={countryOption.country}>
                              {countryOption.country}
                            </option>
                          ))}
                        </select>
                        <Pencil className="pointer-events-none absolute right-0 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-0" />
                      </div>
	                      <div className="flex h-8 min-w-0 items-center text-sm font-semibold text-gray-900">
	                        {pricingDraft.currency_code}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    <div className="group relative w-[9rem]">
                      <span className="pointer-events-none absolute left-0 top-3.5 -translate-y-1/2 text-sm font-semibold text-gray-400">
                        {getCurrencySymbol(pricingDraft.currency_code || funnelCurrencySymbol)}
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={focusedPriceField === `${pricingKey}:price` ? pricingDraft.price : formatPriceInput(pricingDraft.price)}
                        onFocus={() => setFocusedPriceField(`${pricingKey}:price`)}
                        onBlur={() => {
                          updateFunnelPricingDraft(funnel, { price: normalizeEditablePrice(pricingDraft.price) });
                          setFocusedPriceField(null);
                        }}
                        onChange={(event) => updateFunnelPricingDraft(funnel, { price: sanitizePriceInput(event.target.value) })}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.currentTarget.blur();
                          }
                        }}
                        disabled={isSavingPricing}
                        placeholder={funnelPrice === null ? "Pendiente" : undefined}
                        className="h-7 w-full border-0 border-b border-transparent bg-transparent pl-5 pr-5 text-left text-sm font-bold text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-[#3B4A9F] disabled:text-gray-400"
                      />
                      <Pencil className="pointer-events-none absolute right-0 top-3.5 h-3.5 w-3.5 -translate-y-1/2 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-0" />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <a
                      href={buildEditorUrl(ownerId, funnel.website_id)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-xs font-semibold text-gray-700 transition-colors hover:border-fluxi hover:text-fluxi"
                    >
                      Abrir
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderFunnelContextSection = () => {
    if (!funnelContext || !selectedFunnelId) return null;

    const contextItems = getFunnelContextItems();
    const firstOwnerId = contextItems[0]?.parentProduct.owner_id;

    return (
      <section className="overflow-hidden rounded-2xl border border-[#DDE2F1] bg-white shadow-sm">
        <div className="border-b border-[#E8ECF7] bg-[#F8FAFF] px-6 py-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700">
                <Layers3 className="h-3.5 w-3.5" />
                Viendo productos de {funnelContext.website_name || "este embudo"}
              </div>
              <h2 className="text-xl font-bold text-gray-950">Productos de este embudo</h2>
              <p className="mt-1 max-w-3xl text-sm text-gray-500">
                Estos son los productos conectados a este embudo. Algunos pueden estar compartidos con otros embudos.
              </p>
            </div>
            {firstOwnerId && (
              <a
                href={buildEditorUrl(firstOwnerId, funnelContext.website_id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-[#DDE2F1] bg-white px-4 text-sm font-semibold text-gray-700 transition-colors hover:border-fluxi hover:text-fluxi"
              >
                Volver al editor
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>

        <div className="p-5">
          {contextItems.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-5 text-sm text-gray-500">
              No encontramos productos conectados a este embudo en esta respuesta.
            </div>
          ) : (
            <div className="grid gap-3 xl:grid-cols-2">
              {contextItems.map((item) => {
                const sharedFunnelsCount = item.linkedFunnels.length;
                const sharedLabel = sharedFunnelsCount > 1
                  ? `Compartido en ${sharedFunnelsCount} embudos`
                  : "Solo este embudo";

                return (
                  <article
                    key={`${item.contextProduct.product_id}-${item.contextProduct.product_type}`}
                    className={`rounded-2xl border bg-white p-4 transition-colors ${
                      item.isSelected ? "border-blue-300 shadow-[0_0_0_3px_rgba(59,130,246,0.12)]" : "border-[#E5E7EB] shadow-sm"
                    }`}
                  >
                    <div className="flex gap-4">
                      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
                        {item.productImage ? (
                          <img
                            src={item.productImage}
                            alt={item.displayProduct.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-gray-400">
                            Sin imagen
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600">
                            {getProductRoleLabel(item.contextProduct.product_type)}
                          </span>
                          <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                            item.isVersion ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"
                          }`}>
	                            {item.isVersion ? "Oferta migrada" : "Producto catálogo"}
                          </span>
                          {item.isSelected && (
                            <span className="rounded-full bg-[#EEF3FF] px-2.5 py-1 text-[11px] font-semibold text-[#3B4A9F]">
                              Producto abierto desde el editor
                            </span>
                          )}
                        </div>

                        <h3 className="line-clamp-2 text-sm font-bold text-gray-950">{item.displayProduct.name}</h3>
                        <p className="mt-1 text-xs text-gray-400">{item.contextProduct.product_id}</p>

                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                          <span className="rounded-full bg-gray-50 px-3 py-1.5 font-semibold text-gray-700">
                            {formatCurrency(item.effectivePrice, item.currencySymbol)}
                          </span>
                          <span className={`rounded-full px-3 py-1.5 font-semibold ${
                            item.hasOfferPrice ? "bg-emerald-50 text-emerald-700" : "bg-gray-50 text-gray-700"
                          }`}>
                            {item.offerPriceLabel}
                          </span>
                          <span className="rounded-full bg-gray-50 px-3 py-1.5 font-semibold text-gray-700">
                            {item.countryCode}
                          </span>
                          <span className="rounded-full bg-gray-50 px-3 py-1.5 font-semibold text-gray-700">
                            {item.currencyCode}
                          </span>
                          <span className="rounded-full bg-blue-50 px-3 py-1.5 font-semibold text-blue-700">
                            {sharedLabel}
                          </span>
                        </div>
                      </div>

                        <button
                          type="button"
                          onClick={() => {
                            setShowAllProductImages(false);
                            setProductChildrenModal(item.parentProduct);
                          }}
                        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-xl border border-[#DDE2F1] bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:border-fluxi hover:text-fluxi"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Ver detalle
                      </button>
                    </div>
                  </article>
                );
		                              })}
		                            </div>
		                          )}
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => setShowAllProductsInFunnelMode((current) => !current)}
              className="inline-flex h-10 items-center justify-center rounded-xl border border-[#DDE2F1] bg-white px-4 text-sm font-semibold text-gray-700 transition-colors hover:border-fluxi hover:text-fluxi"
            >
              {showAllProductsInFunnelMode ? "Ocultar inventario" : "Ver todos los productos"}
            </button>
          </div>
        </div>
      </section>
    );
  };

  return (
	    <div className="space-y-5">
	      {renderFunnelContextSection()}
	      {(!funnelContext || !selectedFunnelId || showAllProductsInFunnelMode) && (
	      <div>
        <div className="mb-3 flex flex-col gap-1">
          <h2 className="text-lg font-bold text-gray-950">Todos los productos</h2>
          {funnelContext && (
            <p className="text-sm text-gray-500">
              Estás viendo productos relacionados con {funnelContext.website_name || "este embudo"}. Las filas marcadas pertenecen a este embudo.
            </p>
          )}
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="w-[42%] px-6 py-3 text-left text-sm font-medium text-gray-500">
                Producto
              </th>
              <th scope="col" className="w-[18%] px-6 py-3 text-left text-sm font-medium text-gray-500">
	                Precio
              </th>
              <th scope="col" className="w-[20%] px-6 py-3 text-left text-sm font-medium text-gray-500">
	                Mercado
              </th>
              <th scope="col" className="w-[20%] px-6 py-3 text-left text-sm font-medium text-gray-500">
                Embudos vinculados
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {productsState.products.map((product) => {
              const activeCountryCode = product.country_code || country || "CO";
              const activeCurrencyCode = getCurrencyByCountry(activeCountryCode, product.currency_code);
              const currencySymbol = getCurrencySymbol(activeCurrencyCode || activeCountryCode || country);
              const linkedFunnelsCount = getLinkedFunnelsCount(product);
              const hasLinkedFunnels = linkedFunnelsCount > 0;
              const linkedCountryCodes = getProductLinkedCountries(product);
              const visibleCountryCodes = linkedCountryCodes.slice(0, 2);
              const hiddenCountryCount = Math.max(0, linkedCountryCodes.length - visibleCountryCodes.length);
              const productBelongsToCurrentFunnel = isProductInSelectedFunnel(product);
              const selectedProductRow = isSelectedProductRow(product);

              return (
                <tr
                  key={product?.id || ""}
                  className={`cursor-pointer hover:bg-gray-50 ${selectedProductRow ? "bg-blue-50/60" : ""}`}
                  onClick={() => startEditing(product)}
                >
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <img
                        src={(product && product.images && product.images.length > 0) ? product.images[0] : ""}
                        alt={product.name}
                        className="w-10 h-10 rounded-md flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 line-clamp-2">{product.name}</span>
                          {productBelongsToCurrentFunnel && (
                            <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                              En este embudo
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div>
                      <p>{formatCurrency(Number(product.price), currencySymbol)}</p>
                      <p className="mt-1 text-xs text-gray-400">
                        {activeCurrencyCode}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    <div className="flex flex-wrap gap-1.5">
	                      {visibleCountryCodes.map((countryCode) => (
	                        <span
	                          key={countryCode}
	                          title={getCountryName(countryCode)}
	                          className="inline-flex h-7 items-center rounded-full border border-gray-100 bg-gray-50 px-2.5 text-xs font-semibold text-gray-600"
	                        >
	                          {countryCode}
	                        </span>
	                      ))}
                      {hiddenCountryCount > 0 && (
                        <span className="inline-flex h-7 items-center rounded-full border border-blue-100 bg-blue-50 px-2.5 text-xs font-semibold text-blue-700">
                          +{hiddenCountryCount}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {hasLinkedFunnels ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setProductChildrenModal(product);
                        }}
                        className="mt-2 inline-flex h-8 items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 text-xs font-semibold text-blue-700 transition-colors hover:border-blue-200 hover:bg-blue-100"
                      >
                        <Layers3 className="h-3.5 w-3.5" />
                        {getLinkedFunnelsLabel(product)}
                      </button>
                    ) : (
                      <span className="mt-2 inline-flex h-8 items-center rounded-full border border-gray-100 bg-gray-50 px-3 text-xs font-semibold text-gray-500">
                        Sin embudos
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
	        </table>
		      </div>
		      {footer}
        </div>
	      </div>
	      )}
	      <AnimatePresence>
        {productChildrenModal && (
          <motion.div
            className="fixed inset-0 !mt-0 z-[80] flex items-center justify-center bg-slate-950/70 px-4 py-6 backdrop-blur-md"
            onClick={() => {
              setPendingSharedProductSave(null);
              setShowAllProductImages(false);
              setProductChildrenModal(null);
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <motion.div
              className="relative max-h-[calc(100vh-3rem)] w-full max-w-6xl overflow-hidden rounded-[22px] bg-[#F8FAFC] shadow-[0_24px_80px_rgba(15,23,42,0.35)] ring-1 ring-white/20"
              onClick={(event) => event.stopPropagation()}
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              {(() => {
	                const directFunnels = mergeSelectedFunnelContext(
	                  getDirectLinkedFunnels(productChildrenModal),
	                  productChildrenModal
	                );
	                const childProducts = productChildrenModal.child_products || [];
		                const allLinkedFunnels = [
	                  ...directFunnels,
	                  ...childProducts.flatMap((childProduct) => getChildLinkedFunnels(childProduct)),
	                ];
	                const selectedFunnel = selectedFunnelId
	                  ? allLinkedFunnels.find((funnel) => funnel.website_id === selectedFunnelId)
	                  : undefined;
	                const baseDraft = getBaseDraft(productChildrenModal);
		                const baseCountryCode = baseDraft.country_code || productChildrenModal.country_code || country || "CO";
		                const baseCurrencyCode = baseDraft.currency_code || getCurrencyByCountry(baseCountryCode, productChildrenModal.currency_code);
		                const baseCurrencySymbol = getCurrencySymbol(baseCurrencyCode || baseCountryCode || country);
		                const isSavingBase = savingBaseProducts.has(productChildrenModal.id);
		                const baseHasChanges = baseProductHasChanges(productChildrenModal);
		                const changedFunnelCount = directFunnels.filter(funnelPricingHasChanges).length;
		                const modalHasChanges = baseHasChanges || changedFunnelCount > 0;
		                const isSavingModal = isSavingBase || directFunnels.some((funnel) => savingFunnelPricing.has(getFunnelPricingKey(funnel)));
			                const directFunnelsLabel = directFunnels.length === 1 ? "1 embudo usa este producto" : `${directFunnels.length} embudos usan este producto`;
			                const productImages = productChildrenModal.images?.filter(Boolean) || [];
		                const productImage = productImages[0];
		                const visibleProductImages = showAllProductImages ? productImages : productImages.slice(0, 3);
		                const hiddenProductImagesCount = Math.max(0, productImages.length - visibleProductImages.length);
		                const baseCountryName = getCountryName(baseCountryCode);

                return (
                  <>
		                    <button
		                      type="button"
		                      onClick={() => {
		                        setPendingSharedProductSave(null);
		                        setShowAllProductImages(false);
		                        setProductChildrenModal(null);
		                      }}
		                      className="absolute right-4 top-4 z-30 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-gray-400 shadow-sm ring-1 ring-gray-100 transition-colors hover:bg-gray-100 hover:text-gray-700"
		                      aria-label="Cerrar"
		                    >
		                      <X className="h-5 w-5" />
		                    </button>

			                    <div className="max-h-[calc(100vh-3rem)] overflow-y-auto bg-[#F8FAFC]">
	                      <div>
                        <motion.section
                          layout
	                          className="overflow-hidden bg-white"
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }}
                        >
		                          <div className="grid gap-0 lg:grid-cols-[20rem_minmax(0,1fr)]">
		                            <div className="border-b border-[#E8ECF7] bg-[#F6F8FF] p-4 lg:border-b-0 lg:border-r">
			                              <div className="group mb-4">
		                                <label className="mb-1 block text-[11px] font-semibold uppercase text-gray-400">Nombre</label>
		                                <div className="relative">
		                                  <input
		                                    type="text"
		                                    value={baseDraft.name}
		                                    onChange={(event) => updateBaseDraft(productChildrenModal, { name: event.target.value })}
		                                    className="min-h-11 w-full border-0 border-b border-transparent bg-transparent px-0 pr-9 text-sm font-bold leading-snug text-gray-950 outline-none transition-colors focus:border-[#3B4A9F]"
		                                  />
		                                  <Pencil className="pointer-events-none absolute right-1 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-0" />
		                                </div>
		                                <p className="mt-1 truncate text-xs text-gray-400">{productChildrenModal.id}</p>
		                              </div>
		                              <motion.div layout className="flex flex-col gap-4 px-1">
		                                <motion.div
		                                  layout
		                                  className={showAllProductImages ? "order-1" : "order-2"}
		                                  transition={{ type: "spring", stiffness: 420, damping: 34 }}
		                                >
		                                  <div className="grid gap-3">
		                                    <div className="group relative border-b border-[#E8ECF7] pb-3">
		                                      <p className="text-[11px] font-semibold uppercase text-gray-400">Precio</p>
		                                      <div className="relative mt-1">
		                                        <span className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 text-base font-bold text-gray-400">
		                                          {baseCurrencySymbol}
		                                        </span>
		                                        <input
		                                          type="text"
		                                          inputMode="decimal"
		                                          value={
		                                            focusedPriceField === `${productChildrenModal.id}:base-price`
		                                              ? baseDraft.price
		                                              : formatPriceInput(baseDraft.price)
		                                          }
		                                          onFocus={() => setFocusedPriceField(`${productChildrenModal.id}:base-price`)}
		                                          onBlur={() => {
		                                            updateBaseDraft(productChildrenModal, { price: normalizeEditablePrice(baseDraft.price) });
		                                            setFocusedPriceField(null);
		                                          }}
		                                          onChange={(event) => updateBaseDraft(productChildrenModal, { price: sanitizePriceInput(event.target.value) })}
		                                          className="h-8 w-full border-0 border-b border-transparent bg-transparent pl-5 pr-8 text-base font-bold text-gray-950 outline-none transition-colors focus:border-[#3B4A9F]"
		                                        />
		                                        <Pencil className="pointer-events-none absolute right-1 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-0" />
		                                      </div>
		                                    </div>
		                                    <div className="group relative">
		                                      <p className="text-[11px] font-semibold uppercase text-gray-400">Mercado</p>
		                                      <div className="relative mt-1">
		                                        <select
		                                          value={baseDraft.country_code}
		                                          onChange={(event) => {
		                                            const countryCode = event.target.value;
		                                            const currencyCode = getCurrencyByCountry(countryCode, baseDraft.currency_code);
		                                            if (countryCode !== baseDraft.country_code || currencyCode !== baseDraft.currency_code) {
		                                              setPendingCurrencyChange({
		                                                scope: "base",
		                                                product: productChildrenModal,
		                                                country_code: countryCode,
		                                                currency_code: currencyCode,
		                                              });
		                                            }
		                                          }}
		                                          className="h-8 w-full appearance-none border-0 border-b border-transparent bg-transparent px-0 pr-8 text-base font-bold text-gray-950 outline-none transition-colors focus:border-[#3B4A9F]"
		                                        >
		                                          {countryOptions.map((countryOption) => (
		                                            <option key={countryOption.country} value={countryOption.country}>
		                                              {countryOption.name} · {countryOption.currency}
		                                            </option>
		                                          ))}
		                                        </select>
		                                        <Pencil className="pointer-events-none absolute right-1 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-0" />
		                                      </div>
		                                    </div>
		                                  </div>
		                                </motion.div>
		                                <motion.div
		                                  layout
		                                  className={showAllProductImages ? "order-2" : "order-1"}
		                                  transition={{ type: "spring", stiffness: 420, damping: 34 }}
		                                >
		                                  <motion.div layout className="overflow-hidden rounded-2xl bg-white shadow-sm">
		                                    {productImage ? (
		                                      <motion.img
		                                        layout
		                                        src={productImage}
		                                        alt={productChildrenModal.name}
		                                        className="aspect-square w-full object-cover"
		                                      />
		                                    ) : (
		                                      <div className="flex aspect-square w-full items-center justify-center bg-[#EEF3FF] text-sm font-semibold text-[#3B4A9F]">
		                                        Sin imagen
		                                      </div>
		                                    )}
		                                  </motion.div>
		                                  {visibleProductImages.length > 0 && (
		                                    <motion.div
		                                      layout
		                                      className={`mt-3 grid grid-cols-4 gap-2 ${showAllProductImages ? "max-h-56 overflow-y-auto pr-1" : ""}`}
		                                      transition={{ type: "spring", stiffness: 420, damping: 34 }}
		                                    >
		                                      <AnimatePresence initial={false}>
		                                        {visibleProductImages.map((imageUrl, index) => (
		                                          <motion.div
		                                            key={`${imageUrl}-${index}`}
		                                            initial={{ opacity: 0, x: -18, filter: "grayscale(1) saturate(0.6)" }}
		                                            animate={{ opacity: 1, x: 0, filter: "grayscale(0) saturate(1)" }}
		                                            exit={{ opacity: 0, x: 14, filter: "grayscale(1) saturate(0.6)" }}
		                                            transition={{ duration: 0.22, ease: "easeOut", delay: showAllProductImages ? index * 0.025 : 0 }}
		                                            className="aspect-square overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-[#E8ECF7]"
		                                          >
		                                            <img
		                                              src={imageUrl}
		                                              alt={`${productChildrenModal.name} ${index + 1}`}
		                                              className="h-full w-full object-cover"
		                                            />
		                                          </motion.div>
		                                        ))}
		                                        {hiddenProductImagesCount > 0 && (
		                                          <motion.button
		                                            key="show-more-images"
		                                            type="button"
		                                            initial={{ opacity: 0, x: -18 }}
		                                            animate={{ opacity: 1, x: 0 }}
		                                            exit={{ opacity: 0, x: 14 }}
		                                            transition={{ duration: 0.22, ease: "easeOut" }}
		                                            onClick={() => setShowAllProductImages(true)}
		                                            className="aspect-square rounded-xl bg-white px-2 text-xs font-semibold text-[#3B4A9F] shadow-sm ring-1 ring-[#E8ECF7]"
		                                          >
		                                            Ver {hiddenProductImagesCount} más
		                                          </motion.button>
		                                        )}
		                                        {showAllProductImages && productImages.length > 3 && (
		                                          <motion.button
		                                            key="hide-extra-images"
		                                            type="button"
		                                            initial={{ opacity: 0, x: -18 }}
		                                            animate={{ opacity: 1, x: 0 }}
		                                            exit={{ opacity: 0, x: 14 }}
		                                            transition={{ duration: 0.22, ease: "easeOut" }}
		                                            onClick={() => setShowAllProductImages(false)}
		                                            className="aspect-square rounded-xl bg-white px-2 text-xs font-semibold text-gray-500 shadow-sm ring-1 ring-[#E8ECF7]"
		                                          >
		                                            Ver menos
		                                          </motion.button>
		                                        )}
		                                      </AnimatePresence>
		                                    </motion.div>
		                                  )}
		                                </motion.div>
		                              </motion.div>
			                            </div>

	                            <div className="p-5">
	                              <div className="mb-4 flex items-start justify-between gap-3">
	                                <div>
	                                  <h3 className="text-base font-bold text-gray-950">Embudos que usan este producto</h3>
	                                  <p className="mt-1 text-sm text-gray-500">
	                                    Estos embudos usan directamente este producto. Cuando exista, verás el precio de oferta específico del embudo.
	                                  </p>
	                                </div>
	                                <span className="shrink-0 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">{directFunnelsLabel}</span>
	                              </div>
	                              {renderFunnelRows(directFunnels, productChildrenModal.owner_id, selectedFunnelId)}
		                      </div>
		                      <div className="pointer-events-none absolute bottom-5 right-7 z-20 flex items-center gap-2 rounded-2xl bg-white/95 p-2 shadow-[0_18px_50px_rgba(15,23,42,0.22)] ring-1 ring-[#E8ECF7] backdrop-blur">
		                        <button
		                          type="button"
		                          onClick={() => resetProductModalDrafts(productChildrenModal)}
		                          disabled={!modalHasChanges || isSavingModal}
		                          className="pointer-events-auto inline-flex h-10 items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900 disabled:bg-gray-100 disabled:text-gray-400"
		                        >
		                          Descartar
		                        </button>
		                        <button
		                          type="button"
		                          onClick={() => void saveProductModalChanges(productChildrenModal)}
		                          disabled={!modalHasChanges || isSavingModal}
		                          className="pointer-events-auto inline-flex h-10 items-center justify-center gap-1.5 rounded-xl bg-[#26317f] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#1f2766] disabled:bg-gray-200 disabled:text-gray-400"
		                        >
		                          <Save className="h-4 w-4" />
		                          {isSavingModal ? "Guardando" : "Guardar"}
		                        </button>
		                      </div>
	                    </div>
                        </motion.section>

                      </div>
                    </div>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
	        )}
	      </AnimatePresence>
	      {pendingSharedProductSave && (() => {
	        const impactedFunnels = getDirectLinkedFunnels(pendingSharedProductSave);
	        const impactedCount = impactedFunnels.length;
	        const isSavingSharedProduct = savingBaseProducts.has(pendingSharedProductSave.id);

	        return (
	          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-[2px]">
	            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
	              <div className="flex items-start gap-3">
	                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
	                  <AlertTriangle className="h-5 w-5" />
	                </div>
	                <div className="min-w-0">
	                  <h3 className="text-lg font-bold text-gray-950">Actualizar producto compartido</h3>
	                  <p className="mt-2 text-sm leading-6 text-gray-600">
	                    Este cambio actualizará {impactedCount} embudos: {getFunnelNamesPreview(impactedFunnels)}.
	                  </p>
	                </div>
	              </div>

	              <div className="mt-6 flex justify-end gap-3">
	                <button
	                  type="button"
	                  onClick={() => setPendingSharedProductSave(null)}
	                  className="h-10 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900"
	                >
	                  Cancelar
	                </button>
	                <button
	                  type="button"
	                  onClick={() => {
	                    const productToSave = pendingSharedProductSave;
	                    setPendingSharedProductSave(null);
	                    void saveBaseProduct(productToSave, true);
	                  }}
	                  disabled={isSavingSharedProduct}
	                  className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-[#26317f] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#1f2766] disabled:bg-gray-200 disabled:text-gray-400"
	                >
	                  <Save className="h-4 w-4" />
	                  {isSavingSharedProduct ? "Actualizando" : "Actualizar producto compartido"}
	                </button>
	              </div>
	            </div>
	          </div>
	        );
	      })()}
	      {pendingCurrencyChange && (
	        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/55 px-4 backdrop-blur-[3px]">
	          <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
	            <div className="flex items-start gap-4">
	              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
	                <AlertTriangle className="h-5 w-5" />
	              </div>
	              <div className="min-w-0">
	                <h3 className="text-lg font-bold text-gray-950">Cambiar moneda de venta</h3>
	                <p className="mt-2 text-sm leading-6 text-gray-600">
	                  Al cambiar la moneda deberás restablecer los precios en las imágenes de tu producto. Las integraciones que no estén disponibles en el país de venta se deshabilitarán.
	                </p>
	                <p className="mt-3 rounded-xl bg-blue-50 px-4 py-3 text-sm font-medium text-blue-900">
	                  Este cambio quedará pendiente hasta que presiones Guardar.
	                </p>
	              </div>
	            </div>

	            <div className="mt-6 flex justify-end gap-3">
	              <button
	                type="button"
	                onClick={() => setPendingCurrencyChange(null)}
	                className="h-10 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900"
	              >
	                Cancelar
	              </button>
	              <button
	                type="button"
	                onClick={applyPendingCurrencyChange}
	                className="h-10 rounded-xl bg-[#26317f] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#1f2766]"
	              >
	                Aceptar
	              </button>
	            </div>
	          </div>
	        </div>
	      )}
	    </div>
	  );
	}
